import { specializationsOf } from "@shared/specializations";
import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import Header from "@user/components/Header";
import Footer from "@user/components/Footer";
import { api } from "@/lib/api";
import { Star, MapPin, Briefcase, Mail, Phone, ArrowLeft, Languages, MessageSquare, Video, PhoneCall } from "lucide-react";
import VerifiedAdvocateBadge from "@user/components/VerifiedAdvocateBadge";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import PriceTag from "@user/components/PriceTag";
import { photoSrc } from "@shared/photoSrc";

export default function LawyerDetail() {
  const { id } = useParams();
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [lawyer, setLawyer] = useState(null);

  useEffect(() => {
    api.get(`/lawyers/${id}`).then((r) => setLawyer(r.data)).catch(() => setLawyer(null));
  }, [id]);

  const go = (type, mode = "scheduled") => {
    if (!user) { login(); return; }
    const q = mode === "instant" ? `?type=${type}&mode=instant` : `?type=${type}`;
    navigate(`/book/${id}${q}`);
  };

  if (!lawyer) {
    return (
      <div className="min-h-screen bg-[#FAFAFA]">
        <Header />
        <div className="max-w-3xl mx-auto p-8 text-center text-slate-500">Loading lawyer…</div>
      </div>
    );
  }

  const initials = lawyer.name.split(" ").slice(-2).map((x) => x[0]).join("");
  const instantOk = !!(lawyer.instant_available ?? lawyer.available);

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <Header />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Link to="/lawyers" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-[#0A2540] mb-4">
          <ArrowLeft size={14} /> Back to lawyers
        </Link>
        <div className="bg-white border border-slate-200 rounded-2xl p-6 md:p-8 relative">
          {instantOk && (
            <div className="absolute top-4 right-4 flex items-center gap-1.5 bg-emerald-50 text-emerald-700 px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider" data-testid="detail-available-badge">
              <span className="adv-pulse-dot" /> Available now
            </div>
          )}
          <div className="flex flex-col md:flex-row gap-6">
            {lawyer.photo ? (
              <img src={photoSrc(lawyer.photo, "md")} alt={lawyer.name} className="h-28 w-28 rounded-2xl object-cover border border-slate-200" />
            ) : (
              <div className="h-28 w-28 rounded-2xl adv-navy text-[#D4AF37] grid place-items-center font-bold text-3xl">
                {initials}
              </div>
            )}
            <div className="flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="text-2xl md:text-3xl font-extrabold text-[#0A2540]" style={{ fontFamily: "Poppins" }}>{lawyer.name}</h1>
                <VerifiedAdvocateBadge lawyer={lawyer} size="md" showLabel />
              </div>
              <p className="text-[#0A2540] font-semibold mt-1" data-testid="lawyer-detail-specializations">{specializationsOf(lawyer).join(" · ") || lawyer.specialization}</p>
              <div className="flex flex-wrap gap-4 mt-3 text-sm text-slate-600">
                <span className="inline-flex items-center gap-1"><MapPin size={14} />{lawyer.city}</span>
                <span className="inline-flex items-center gap-1"><Briefcase size={14} />{lawyer.experience} years experience</span>
                <span className="inline-flex items-center gap-1 bg-amber-50 text-amber-800 px-2 py-0.5 rounded"><Star size={12} fill="currentColor" />{lawyer.rating} ({lawyer.reviews_count} reviews)</span>
              </div>
              {lawyer.languages?.length > 0 && (
                <div className="mt-3 inline-flex items-center gap-2 text-sm text-slate-600">
                  <Languages size={14} /> {lawyer.languages.join(" · ")}
                </div>
              )}
              <p className="mt-4 text-slate-700 leading-relaxed">{lawyer.bio}</p>
            </div>
          </div>

          {/* Pricing + CTAs */}
          <div className="mt-6 pt-6 border-t border-slate-100">
            <p className="text-xs uppercase tracking-wider font-bold text-slate-400 mb-2">Consultation fees (incl. GST)</p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {[
                { key: "chat", label: "Chat", Icon: MessageSquare },
                { key: "audio", label: "Audio", Icon: PhoneCall },
                { key: "video", label: "Video", Icon: Video },
              ].map(({ key, label, Icon }) => (
                <div key={key} className="bg-white border border-slate-200 rounded-2xl p-4">
                  <div className="flex items-center gap-2 text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">
                    <Icon size={14} /> {label}
                  </div>
                  <PriceTag pricing={lawyer?.pricing?.[key]} layout="stacked" size="md" testid={`detail-price-${key}`} />
                </div>
              ))}
            </div>
            <div className="mt-3 grid md:grid-cols-3 gap-2">
              <Button onClick={() => go("chat", instantOk ? "instant" : "scheduled")} data-testid="cta-chat-now" className="rounded-xl adv-btn-primary font-bold h-full flex-col gap-1 py-4">
                <MessageSquare size={18} /> Chat
              </Button>
              <Button onClick={() => go("audio", instantOk ? "instant" : "scheduled")} data-testid="cta-audio-call" className="rounded-xl adv-btn-gold font-bold h-full flex-col gap-1 py-4">
                <PhoneCall size={18} /> Audio
              </Button>
              <Button onClick={() => go("video", instantOk ? "instant" : "scheduled")} data-testid="cta-video-call" className="rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold h-full flex-col gap-1 py-4">
                <Video size={18} /> Video
              </Button>
            </div>
            {instantOk && (
              <p className="md:col-span-3 text-[11px] text-center text-emerald-700 font-semibold">
                ⚡ Available now — your call starts right after payment. <button onClick={() => go("video", "scheduled")} className="underline font-bold hover:text-emerald-900">Or book for later</button>
              </p>
            )}
            {!instantOk && lawyer.next_available && (
              <p className="md:col-span-3 text-[11px] text-center text-slate-500 font-semibold" data-testid="detail-next-available">
                Next available: {new Date(`${lawyer.next_available.date}T00:00:00`).toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short" })} · {lawyer.next_available.time} IST
              </p>
            )}
          </div>

          {(lawyer.email || lawyer.phone) && (
            <div className="mt-6 pt-6 border-t border-slate-100 flex flex-wrap gap-5 text-sm text-slate-500">
              {lawyer.email && <span className="inline-flex items-center gap-1"><Mail size={14} />{lawyer.email}</span>}
              {lawyer.phone && <span className="inline-flex items-center gap-1"><Phone size={14} />{lawyer.phone}</span>}
            </div>
          )}
        </div>

        {/* Reviews (stubbed) */}
        <div className="mt-8">
          <h2 className="text-xl font-extrabold text-[#0A2540]" style={{ fontFamily: "Poppins" }}>What clients say</h2>
          <div className="mt-3 grid md:grid-cols-2 gap-3">
            {[
              { name: "Amit R.", text: "Clear advice, no jargon. Resolved my issue in a single call.", rating: 5 },
              { name: "Shilpa K.", text: "Very professional and responsive. Helped me understand the bail process.", rating: 5 },
            ].map((r) => (
              <div key={r.name} className="bg-white border border-slate-200 rounded-2xl p-5">
                <div className="flex items-center gap-1 text-amber-500">{Array.from({ length: r.rating }).map((_, j) => <Star key={`${r.name}-star-${j}`} size={14} fill="currentColor" />)}</div>
                <p className="mt-2 text-sm text-slate-700 leading-relaxed">"{r.text}"</p>
                <p className="mt-2 text-xs font-semibold text-slate-500">— {r.name}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
